import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import ApexGraph from "../components/ApexGraph";
import ErrorBoundary from "../components/ErrorBoundary";
import HeatmapChart from "../components/HeatmapChart";
import useHistoricalData from "../hooks/useHistoricalData";
import useHeatmapData from "../hooks/useHeatmapData";
import SankeyDiagram from "../components/SankeyDiagram";
import DrillDownPieChart from "../components/DrillDownPieChart";
import SolarGraph from "../components/SolarGraph";
import SolarProductionComparison from "../components/SolarProductionComparison";
import ChartBurgerMenu from "../components/ChartBurgerMenu";
import ComparisonGraph from "../components/ComparisonGraph";
import OnOffChart from "../components/OnOffChart";
import {
  getMachineType,
  getColumnVisibility,
  getMainEnergyLabel,
} from "../utils/machineTypeDetector";
import api from "../utils/api";
import { getConsumerInfo } from "../controllers/userController";
import {
  buildEnergyBreakdownRows,
  fetchDailyEnergySeries,
  fetchEnergyHierarchy,
} from "../utils/energyReportData";
import { fetchHeatmapEnergyData } from "../utils/energyReportHeatmap";
import { generateEnergyBreakdownPdf } from "../utils/energyReportPdf";
import EnergyReportPdfDialog from "../components/EnergyReportPdfDialog";
import {
  buildCostRows,
  buildScopeCostSummary,
  fetchReportPricing,
  fetchScopeSummary,
} from "../utils/energyReportCost";
import "./HistoricalTab.css";

/** Parse numeric price from API or storage; null if missing/invalid */
function parsePriceNumber(value) {
  if (value == null || value === "") return null;
  const p = parseFloat(value);
  return Number.isFinite(p) ? p : null;
}

function extractStatValue(stat) {
  if (stat == null) return null;
  if (typeof stat === "object") {
    const value = stat.value ?? stat.avg ?? stat.min ?? stat.max;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  const parsed = Number(stat);
  return Number.isFinite(parsed) ? parsed : null;
}

function extractStatTimestamp(stat) {
  if (!stat || typeof stat !== "object") return null;
  return stat.timestamp || null;
}

function findClosestTimestamp(rawLabels, values, targetValue) {
  const numericTarget = Number(targetValue);
  if (!Number.isFinite(numericTarget)) return null;
  if (!Array.isArray(values) || !values.length) return null;

  let bestIndex = -1;
  let smallestDistance = Number.POSITIVE_INFINITY;

  values.forEach((value, index) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return;

    const distance = Math.abs(numericValue - numericTarget);
    if (distance < smallestDistance) {
      smallestDistance = distance;
      bestIndex = index;
    }
  });

  if (bestIndex < 0) return null;
  return rawLabels?.[bestIndex] || null;
}

const quickRanges = [
  { label: "3D", value: 3 },
  { label: "7D", value: 7 },
  { label: "30D", value: 30 },
];

const comparisonMetricOptions = [
  {
    label: "Power",
    value: "power_kw",
    unit: "kW",
    matchers: ["kW", "power_kw", "power_kw", "power", "_power"], // Prioritize kW, removed TAP to avoid duplication
  },
  {
    label: "Energy",
    value: "energy",
    unit: "kWh",
    matchers: ["energy", "kWh", "_energy", "_kwh"],
  },
  {
    label: "Voltage",
    value: "voltage",
    unit: "V",
    matchers: ["voltage", "[v]", "_V", "V"],
  },
  {
    label: "Power Factor",
    value: "power_factor",
    unit: "",
    matchers: ["power factor", "power_factor", "pf", "_PF", "PF"],
  },
  {
    label: "Current",
    value: "current",
    unit: "A",
    matchers: ["current", "[a]", "_A", "A"],
  },
  {
    label: "Frequency",
    value: "frequency",
    unit: "Hz",
    matchers: ["frequency", "[hz]", "_Hz", "Hz"],
  },
  {
    label: "THD Voltage",
    value: "THD_V",
    unit: "V",
    matchers: ["THD_V", "thd voltage", "voltage thd", "_THD_V"],
  },
  {
    label: "THD Current",
    value: "THD_I",
    unit: "A",
    matchers: ["THD_I", "thd current", "current thd", "_THD_I"],
  },
  {
    label: "TAP",
    value: "TAP",
    unit: "kW",
    matchers: ["TAP", "total active power", "total_active_power", "_TAP"],
  },
];

const phaseMetricRegex = /phase\s*-?_?(1|2|3)/i;

const knownMetricSuffixes = [
  "power_kw",
  "power_factor",
  "frequency",
  "voltage",
  "current",
  "usage",
  "energy",
  "thd_voltage",
  "thd_current",
  "thd_v",
  "thd_i",
  "tap",
  "kwh",
  "kw",
  "hz",
  "pf",
  "v",
  "a",
];

const parseCompositeMetric = (metricKey) => {
  if (!metricKey) return { machineName: "", metricName: "" };
  const lower = String(metricKey).toLowerCase();

  const sortedSuffixes = [...knownMetricSuffixes].sort(
    (a, b) => b.length - a.length,
  );

  for (const suffix of sortedSuffixes) {
    const token = `_${suffix}`;
    if (lower.endsWith(token)) {
      return {
        machineName: metricKey.slice(0, metricKey.length - token.length),
        metricName: metricKey.slice(metricKey.length - suffix.length),
      };
    }
  }

  const idx = metricKey.lastIndexOf("_");
  if (idx === -1) return { machineName: metricKey, metricName: "" };
  return {
    machineName: metricKey.slice(0, idx),
    metricName: metricKey.slice(idx + 1),
  };
};

const formatMetricLabel = (metric) => {
  if (!metric) return "";
  const { machineName, metricName } = parseCompositeMetric(metric);
  if (machineName && metricName) {
    const prettyMetricName = String(metricName).replace(/_/g, " ");
    return `${machineName} (${prettyMetricName})`;
  }
  return metric;
};

const ChartStatus = ({ type = "empty", title, message, actionLabel, onAction }) => {
  const isLoading = type === "loading";
  const isError = type === "error";

  return (
    <div className={`historical-chart-status historical-chart-status-${type}`}>
      {isLoading ? (
        <div className="historical-chart-spinner" aria-hidden="true" />
      ) : (
        <div className="historical-chart-status-icon" aria-hidden="true">
          {isError ? "!" : "i"}
        </div>
      )}
      <div className="historical-chart-status-title">{title}</div>
      {message ? (
        <div className="historical-chart-status-message">{message}</div>
      ) : null}
      {actionLabel && onAction ? (
        <button
          className="historical-chart-status-action"
          type="button"
          onClick={onAction}
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
};

const HistoricalTab = () => {
  const {
    mode,
    setMode,
    dateRange,
    graphDateRange,
    setDateRangeFromStrings,
    setGraphDateRangeFromStrings,
    selectedMetric,
    setSelectedMetric,
    loading,
    error,
    chartData,
    metrics,
    seriesByMetric,
    applyQuickRange,
    refetch,
    fetchSummaryData,
    summaryData,
    summaryLoading,
    selectedMachineNode,
    setSelectedMachineNode, // Added to fix ESLint error
    hierarchyData, // New: Get hierarchy
    machineColors,
    rawLabels,
  } = useHistoricalData();

  const [viewMode, setViewMode] = useState("chart"); // "chart" or "heatmap"
  const [comparisonMetric, setComparisonMetric] = useState("power_kw");
  const [comparisonSelections, setComparisonSelections] = useState({});
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [selectAllActive, setSelectAllActive] = useState(false);
  const [userSelectionLocked, setUserSelectionLocked] = useState(false);
  const [currencyMode, setCurrencyMode] = useState("Energy"); // "Energy" or "PKR"
  const [graphType, setGraphType] = useState("line"); // Default to line
  const isEnergyMetric = comparisonMetric === "energy";

  // Energy metric is only meaningful as a bar graph — force it when selected.
  // When switching away from Energy, reset to `line` only if the previous
  // metric was Energy (so we don't override an explicit user choice).
  const prevComparisonRef = useRef(comparisonMetric);
  useEffect(() => {
    const prev = prevComparisonRef.current;
    if (comparisonMetric === "energy") {
      setGraphType("bar");
    } else if (prev === "energy" && comparisonMetric !== "energy") {
      // We only auto-reset when coming from energy → other
      setGraphType("line");
    }
    prevComparisonRef.current = comparisonMetric;
  }, [comparisonMetric]);
  const [solarDetected, setSolarDetected] = useState(false);
  const [solarData, setSolarData] = useState(null);
  const [solarLoading, setSolarLoading] = useState(false);
  const [solarGranularity, setSolarGranularity] = useState("hourly");
  const [solarProductionData, setSolarProductionData] = useState(null);
  const [solarProductionLoading, setSolarProductionLoading] = useState(false);
  const [selectedSolarMachine, setSelectedSolarMachine] = useState("");
  const [lpfData, setLpfData] = useState(null);
  const [lpfLoading, setLpfLoading] = useState(false);
  const [mdiData, setMdiData] = useState(null);
  const [mdiLoading, setMdiLoading] = useState(false);
  const [childGraphLoaders, setChildGraphLoaders] = useState({});
  /** Pricing from `client_profiles` via GET /get_customer_info (gen_unitprice, unit_price, …) */
  const [clientProfilePricing, setClientProfilePricing] = useState(null);

  // Compute solar machines list from hierarchyData
  const solarMachines = useMemo(() => {
    const flatten = (nodes) => {
      let flat = [];
      if (!Array.isArray(nodes)) return flat;
      nodes.forEach((node) => {
        if (node.name) flat.push(node);
        if (node.children) flat = flat.concat(flatten(node.children));
      });
      return flat;
    };

    const allNodes = flatten(hierarchyData || []);
    const solarList = [];
    const seen = new Set();

    allNodes.forEach((node) => {
      const category = (node.category || "").toUpperCase();
      if (
        node.name &&
        !seen.has(node.name) &&
        category === "SOLAR"
      ) {
        solarList.push(node);
        seen.add(node.name);
      }
    });

    return solarList;
  }, [hierarchyData]);

  // Set default selected solar machine when solarMachines load
  useEffect(() => {
    if (solarMachines.length > 0 && !selectedSolarMachine) {
      setSelectedSolarMachine(solarMachines[0].name);
    }
  }, [solarMachines, selectedSolarMachine]);
  const [pdfReportLoading, setPdfReportLoading] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const comparisonChartRef = useRef(null);
  const machineListRef = useRef(null);

  const scrollMachineList = (direction) => {
    if (machineListRef.current) {
      const scrollAmount = 300;
      machineListRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const updateChildGraphLoading = useCallback((key, isLoading) => {
    setChildGraphLoaders((prev) => {
      if (prev[key] === isLoading) {
        return prev;
      }
      return {
        ...prev,
        [key]: isLoading,
      };
    });
  }, []);

  // Isolation: Maintain a stable range for other charts that doesn't follow 'second' mode
  const [stableDateRange, setStableDateRange] = useState(dateRange);

  useEffect(() => {
    if (mode !== "second") {
      setStableDateRange(dateRange);
    }
  }, [dateRange, mode]);

  useEffect(() => {
    const username = localStorage.getItem("username");
    if (!username) {
      setClientProfilePricing(null);
      return;
    }
    getConsumerInfo(username)
      .then((data) => {
        setClientProfilePricing({
          unit_price: data.unit_price,
          gen_unitprice:
            data.gen_unitprice ?? data.gen_unitPrice ?? null,
          solar_unitprice:
            data.solar_unitprice ?? data.solar_unitPrice ?? null,
        });
      })
      .catch(() => {
        setClientProfilePricing(null);
      });
  }, []);

  // Determine machine type from selected node
  const selectedMachineType = useMemo(() => {
    if (!selectedMachineNode) return "building";
    return getMachineType(selectedMachineNode);
  }, [selectedMachineNode]);

  // Get column visibility based on machine type
  const columnVisibility = useMemo(() => {
    return getColumnVisibility(selectedMachineType);
  }, [selectedMachineType]);

  // Get main energy card label
  const mainEnergyLabel = useMemo(() => {
    return getMainEnergyLabel(selectedMachineType);
  }, [selectedMachineType]);

  const unitPrice = useMemo(() => {
    const category = (selectedMachineNode?.category || "").toUpperCase();
    const fromDb =
      category === "GENERATOR"
        ? parsePriceNumber(
          clientProfilePricing?.gen_unitprice ??
          clientProfilePricing?.gen_unitPrice,
        )
        : parsePriceNumber(clientProfilePricing?.unit_price);
    if (fromDb != null) return fromDb;
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      let priceStr = user?.unit_price;
      if (category === "GENERATOR") {
        priceStr =
          user?.gen_unitPrice ?? user?.gen_unitprice ?? priceStr;
      }
      const price = parseFloat(priceStr);
      return Number.isFinite(price) ? price : 0;
    } catch {
      return 0;
    }
  }, [selectedMachineNode, clientProfilePricing]);

  useEffect(() => {
    if (selectedMetric) {
      const { machineName } = parseCompositeMetric(selectedMetric);
      fetchSummaryData(machineName, comparisonMetric);
    }
  }, [selectedMetric, comparisonMetric, fetchSummaryData, mode]);

  // Solar Detection API Call
  // Solar Detection API Call
  useEffect(() => {
    const checkSolarDetection = async () => {
      const username = localStorage.getItem("username");

      console.log("Checking Solar Detection for machine:", selectedSolarMachine);

      if (!username || !selectedSolarMachine) {
        setSolarLoading(false);
        setSolarDetected(false);
        return;
      }

      const formatDate = (date) => {
        if (!date) return "";
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, "0");
        const day = `${date.getDate()}`.padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      try {
        setSolarLoading(true);
        const response = await api.get("/solar-data", {
          params: {
            username,
            machine_name: selectedSolarMachine,
            start_date: formatDate(stableDateRange.start),
            end_date: formatDate(stableDateRange.end),
            mode: solarGranularity,
          },
        });

        // If API says detected, keep it true and set data
        const apiDetected = response.data?.solar_detected || false;

        if (apiDetected) {
          setSolarDetected(true);
          setSolarData(response.data);
        } else {
          setSolarDetected(false);
          setSolarData(null);
        }
      } catch (error) {
        console.error("Error checking solar detection:", error);
        // Keep UI visible even if data fetch fails, but clear data
        setSolarDetected(true); 
        setSolarData(null);
      } finally {
        setSolarLoading(false);
      }
    };

    checkSolarDetection();
  }, [
    selectedSolarMachine,
    stableDateRange.start,
    stableDateRange.end,
    solarGranularity,
  ]);

  // LPF Data Fetching
  useEffect(() => {
    const fetchLpfData = async () => {
      const username = localStorage.getItem("username");
      const machineName =
        selectedMachineNode?.name ||
        (selectedMetric ? parseCompositeMetric(selectedMetric).machineName : null);

      if (!username || !machineName) {
        setLpfLoading(false);
        setLpfData(null);
        return;
      }

      const formatDateLong = (date) => {
        if (!date) return "";
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, "0");
        const day = `${date.getDate()}`.padStart(2, "0");
        const hours = `${date.getHours()}`.padStart(2, "0");
        const minutes = `${date.getMinutes()}`.padStart(2, "0");
        const seconds = `${date.getSeconds()}`.padStart(2, "0");
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };

      try {
        setLpfLoading(true);
        const response = await api.get("/lpf", {
          params: {
            username,
            machine_name: machineName,
            start: formatDateLong(stableDateRange.start),
            end: formatDateLong(stableDateRange.end),
          },
        });
        setLpfData(response.data);
      } catch (error) {
        console.error("Error fetching LPF data:", error);
        setLpfData(null);
      } finally {
        setLpfLoading(false);
      }
    };

    fetchLpfData();
  }, [
    selectedMetric,
    selectedMachineNode,
    stableDateRange.start,
    stableDateRange.end,
  ]);

  // MDI Data Fetching
  useEffect(() => {
    const fetchMdiData = async () => {
      const username = localStorage.getItem("username");

      if (!username) {
        setMdiLoading(false);
        setMdiData(null);
        return;
      }

      const formatDateLong = (date) => {
        if (!date) return "";
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, "0");
        const day = `${date.getDate()}`.padStart(2, "0");
        const hours = `${date.getHours()}`.padStart(2, "0");
        const minutes = `${date.getMinutes()}`.padStart(2, "0");
        const seconds = `${date.getSeconds()}`.padStart(2, "0");
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };

      try {
        setMdiLoading(true);
        const response = await api.get("/mdi", {
          params: {
            username,
            start: formatDateLong(stableDateRange.start),
            end: formatDateLong(stableDateRange.end),
          },
        });
        setMdiData(response.data);
      } catch (error) {
        console.error("Error fetching MDI data:", error);
        setMdiData(null);
      } finally {
        setMdiLoading(false);
      }
    };

    fetchMdiData();
  }, [
    stableDateRange.start,
    stableDateRange.end,
  ]);

  // Solar Production Data Fetching (Expected vs Actual)
  useEffect(() => {
    const fetchSolarProductionData = async () => {
      const username = localStorage.getItem("username");

      console.log("[Solar Production Fetch] Triggered with:", {
        username,
        selectedSolarMachine,
        solarDetected,
      });

      if (!username || !selectedSolarMachine) {
        setSolarProductionLoading(false);
        setSolarProductionData(null);
        return;
      }

      const formatDate = (date) => {
        if (!date) return "";
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, "0");
        const day = `${date.getDate()}`.padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      try {
        setSolarProductionLoading(true);
        const startFormatted = formatDate(stableDateRange.start);
        const endFormatted = formatDate(stableDateRange.end);

        console.log("[Solar Production API] Calling with params:", {
          username,
          machine_name: selectedSolarMachine,
          start_datetime: startFormatted,
          end_datetime: endFormatted,
        });

        const response = await api.get("/solar-production", {
          params: {
            username,
            machine_name: selectedSolarMachine,
            start_datetime: startFormatted,
            end_datetime: endFormatted,
          },
        });

        console.log("[Solar Production API] Response:", response.data);
        console.log("[Solar Production API] Array lengths:", {
          timestamps: response.data?.timestamps?.length,
          solar_energy_production: response.data?.solar_energy_production?.length,
          energy_negative_kwh: response.data?.energy_negative_kwh?.length,
        });
        console.log("[Solar Production API] First 3 items:", {
          timestamps: response.data?.timestamps?.slice(0, 3),
          solar_energy_production: response.data?.solar_energy_production?.slice(0, 3),
          energy_negative_kwh: response.data?.energy_negative_kwh?.slice(0, 3),
        });

        // Transform API response to match component expectations
        const transformedData = response.data?.timestamps?.map((timestamp, index) => ({
          timestamp,
          expected_generation_kwh: response.data?.solar_energy_production?.[index] || 0,
          actual_generation_kwh: response.data?.energy_negative_kwh?.[index] || 0,
        })) || [];

        console.log("[Solar Production] Transformed data (first 5):", transformedData.slice(0, 5));
        setSolarProductionData({ data: transformedData });
      } catch (error) {
        console.error("Error fetching solar production data:", error);
        setSolarProductionData(null);
      } finally {
        setSolarProductionLoading(false);
      }
    };

    // Only fetch if solar is detected
    if (solarDetected) {
      fetchSolarProductionData();
    }
  }, [
    selectedSolarMachine,
    stableDateRange.start,
    stableDateRange.end,
    solarDetected,
  ]);


  const activeComparisonOption = useMemo(
    () =>
      comparisonMetricOptions.find((opt) => opt.value === comparisonMetric) ||
      comparisonMetricOptions[0],
    [comparisonMetric],
  );

  // No-op for summaryCards, now using direct rendering for tables

  const {
    rows: rawHeatmapRows,
    timeLabels: heatmapTimeLabels,
    minValue: rawHeatmapMin,
    maxValue: rawHeatmapMax,
    loading: heatmapLoading,
    hasData: hasHeatmapData,
    refetch: refetchHeatmap,
  } = useHeatmapData(selectedMetric, stableDateRange);

  const isAnyGraphLoading = useMemo(() => {
    const childLoading = Object.values(childGraphLoaders).some(Boolean);
    return (
      loading ||
      summaryLoading ||
      heatmapLoading ||
      solarLoading ||
      solarProductionLoading ||
      lpfLoading ||
      mdiLoading ||
      childLoading
    );
  }, [
    childGraphLoaders,
    heatmapLoading,
    loading,
    lpfLoading,
    mdiLoading,
    solarLoading,
    solarProductionLoading,
    summaryLoading,
  ]);

  const { heatmapRows, heatmapMin, heatmapMax } = useMemo(() => {
    if (!rawHeatmapRows || !hasHeatmapData)
      return { heatmapRows: [], heatmapMin: 0, heatmapMax: 0 };

    // Check if we need to convert to PKR
    const isPKR = currencyMode === "PKR";
    // Rely on the active category instead of metric string matching
    const isPower =
      activeComparisonOption.value === "power_kw" ||
      activeComparisonOption.value === "tap";

    if (isPKR && isPower) {
      const multiplier = unitPrice;
      const convertedRows = rawHeatmapRows.map((row) => ({
        ...row,
        values: row.values.map((v) =>
          typeof v === "number" ? v * multiplier : v,
        ),
      }));
      return {
        heatmapRows: convertedRows,
        heatmapMin:
          typeof rawHeatmapMin === "number"
            ? rawHeatmapMin * multiplier
            : rawHeatmapMin,
        heatmapMax:
          typeof rawHeatmapMax === "number"
            ? rawHeatmapMax * multiplier
            : rawHeatmapMax,
      };
    }

    return {
      heatmapRows: rawHeatmapRows,
      heatmapMin: rawHeatmapMin,
      heatmapMax: rawHeatmapMax,
    };
  }, [
    rawHeatmapRows,
    hasHeatmapData,
    currencyMode,
    activeComparisonOption,
    unitPrice,
    rawHeatmapMin,
    rawHeatmapMax,
  ]);

  // Consolidate machine list from hierarchy and metrics, add 'Usage' as special option
  const machinesList = useMemo(() => {
    const flatten = (nodes) => {
      let flat = [];
      if (!Array.isArray(nodes)) return flat;
      nodes.forEach((node) => {
        if (node.name) flat.push(node);
        if (node.children) flat = flat.concat(flatten(node.children));
      });
      return flat;
    };

    const allHierarchyNodes = flatten(hierarchyData || []);
    const seenNames = new Set();
    const result = [];

    // 1. Add nodes from hierarchy
    allHierarchyNodes.forEach((node) => {
      const category = (node.category || "").toUpperCase();
      const nodeName = (node.name || "").toUpperCase();
      // Exclude SOLAR and VIRTUAL category nodes from the machine selector
      if (
        node.name &&
        !seenNames.has(node.name) &&
        category !== "SOLAR" &&
        category !== "VIRTUAL" &&
        !nodeName.includes("SOLAR")
      ) {
        result.push(node);
        seenNames.add(node.name);
      }
    });

    // 2. Add machines from metrics as fallback
    metrics.forEach((m) => {
      const { machineName: name } = parseCompositeMetric(m);
      const upperName = name.toUpperCase();
      if (
        name &&
        !seenNames.has(name) &&
        name !== "Date & Time" &&
        !upperName.includes("SOLAR")
      ) {
        result.push({ name, category: "Unknown", children: [] });
        seenNames.add(name);
      }
    });

    // Add 'Usage' as a special option if not already present
    if (!seenNames.has("Usage")) {
      result.unshift({ name: "Usage", category: "Special", children: [] });
      seenNames.add("Usage");
    }

    return result.sort((a, b) => {
      // Keep 'Usage' always at the top
      if (a.name === "Usage") return -1;
      if (b.name === "Usage") return 1;
      const getPriority = (node) => {
        const cat = (node.category || "").toUpperCase();
        if (cat === "GRID") return 0;
        if (cat === "GENERATOR") return 1;
        if (cat === "MACHINE") return 2;
        return 3;
      };

      const priorityA = getPriority(a);
      const priorityB = getPriority(b);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      return a.name.localeCompare(b.name);
    });
  }, [hierarchyData, metrics]);

  // Initialize selectedMachines when machinesList loads
  useEffect(() => {
    if (!machinesList || machinesList.length === 0) return;
    // If already set, do not override user's selection
    if (selectedMachines && selectedMachines.length) return;
    // Default: select Usage + first GRID/MACHINE entries
    const defaults = [];
    if (machinesList.find((m) => m.name === "Usage")) defaults.push("Usage");
    const firstGrid = machinesList.find((m) => (m.category || "").toUpperCase() === "GRID");
    const firstMachine = machinesList.find((m) => (m.category || "").toUpperCase() === "MACHINE");
    if (firstGrid && !defaults.includes(firstGrid.name)) defaults.push(firstGrid.name);
    else if (firstMachine && !defaults.includes(firstMachine.name)) defaults.push(firstMachine.name);
    if (defaults.length) setSelectedMachines(defaults);
  }, [machinesList]);

  useEffect(() => {
    if (!selectAllActive || !machinesList.length) return;

    const allNames = machinesList.map((machine) => machine.name);
    const currentKey = selectedMachines.join("|");
    const nextKey = allNames.join("|");

    if (currentKey !== nextKey) {
      setSelectedMachines(allNames);
    }
  }, [selectAllActive, machinesList, selectedMachines]);

  const availableComparisonMetrics = useMemo(() => {
    if (!metrics.length) return [];
    const isMatch = (metricName) =>
      metricName &&
      activeComparisonOption.matchers.some((matcher) =>
        metricName.toLowerCase().includes(matcher.toLowerCase().trim()),
      );
    // Initial filter based on matchers and exclude phase metrics
    const matched = metrics.filter(
      (metric) => isMatch(metric) && !phaseMetricRegex.test(metric),
    );
    // Deduplicate by machine name (prefix before first underscore)
    const seen = new Set();
    const deduped = [];
    matched.forEach((m) => {
      const { machineName } = parseCompositeMetric(m);
      const upperMachineName = machineName.toUpperCase();
      // Skip if machine is categorized as SOLAR or name contains SOLAR
      const machineNode = machinesList.find(
        (node) => node.name === machineName,
      );
      const isSolar =
        (machineNode &&
          (machineNode.category || "").toUpperCase() === "SOLAR") ||
        upperMachineName.includes("SOLAR");

      if (!seen.has(machineName) && !isSolar) {
        seen.add(machineName);
        deduped.push(m);
      }
    });
    return deduped;
  }, [metrics, activeComparisonOption, machinesList]);

  // Sync selectedMetric with available options for the category
  useEffect(() => {
    if (selectedMachineNode?.name === "Usage") return; // Do not synchronize when Usage is selected

    if (availableComparisonMetrics.length > 0) {
      if (!availableComparisonMetrics.includes(selectedMetric)) {
        // Only override if the currently selected one is NOT in the new list
        const currentMachine = selectedMetric
          ? parseCompositeMetric(selectedMetric).machineName
          : selectedMachineNode?.name || "";

        const sameMachineMetric = availableComparisonMetrics.find(
          (m) =>
            m.startsWith(`${currentMachine}_`) || m.startsWith(currentMachine),
        );
        if (sameMachineMetric) {
          setSelectedMetric(sameMachineMetric);
        } else {
          setSelectedMetric(availableComparisonMetrics[0]);
        }
      }
    }
  }, [
    availableComparisonMetrics,
    selectedMetric,
    setSelectedMetric,
    selectedMachineNode,
  ]);

  // Specific sync for Heatmap when selectedMachineNode changes
  useEffect(() => {
    if (selectedMachineNode?.name === "Usage") return; // Skip sync for Usage
    if (selectedMachineNode && metrics.length > 0) {
      const machineName = selectedMachineNode.name;
      const machineMetrics = metrics.filter((m) =>
        m.startsWith(`${machineName}_`),
      );
      if (machineMetrics.length > 0) {
        // Try to stay on the same metric type if possible, otherwise default to first machine metric
        const currentMetricPart = selectedMetric?.split("_")?.[1] || "";
        const match = machineMetrics.find((m) =>
          m.endsWith(`_${currentMetricPart}`),
        );
        if (match) {
          setSelectedMetric(match);
        } else {
          const preferred = machineMetrics.find(
            (m) =>
              m.toLowerCase().includes("usage") ||
              m.toLowerCase().includes("kw") ||
              m.toLowerCase().includes("power"),
          );
          setSelectedMetric(preferred || machineMetrics[0]);
        }
      }
    }
  }, [selectedMachineNode, metrics, selectedMetric, setSelectedMetric]);

  useEffect(() => {
    // Use functional updater so we don't need comparisonSelections in deps
    // (including it caused an infinite re-render loop).
    if (!availableComparisonMetrics.length) {
      setComparisonSelections((prev) =>
        Object.keys(prev || {}).length === 0 ? prev : {},
      );
      return;
    }
    if (!selectedMachines || selectedMachines.length === 0) {
      setComparisonSelections((prev) =>
        Object.keys(prev || {}).length === 0 ? prev : {},
      );
      return;
    }

    setComparisonSelections((prev) => {
      // Do not overwrite an active multi-selection; only initialize when empty.
      if (Object.keys(prev || {}).length > 0) {
        return prev;
      }
      const next = {};
      availableComparisonMetrics.forEach((metric) => {
        next[metric] = metric === selectedMetric;
      });
      const anyTrue = Object.values(next).some(Boolean);
      if (!anyTrue && availableComparisonMetrics.length) {
        next[availableComparisonMetrics[0]] = true;
      }
      return next;
    });
  }, [availableComparisonMetrics, selectedMetric, selectedMachines]);

  const comparisonSeries = useMemo(() => {
    if (!chartData.labels.length || !availableComparisonMetrics.length) {
      return {
        labels: chartData.labels,
        datasets: [],
        option: activeComparisonOption,
      };
    }
    // Use the explicit keys in comparisonSelections so fallback metrics
    // (added from `metrics`) are respected even if not listed in
    // `availableComparisonMetrics`.
    const selectedMetrics = Object.keys(comparisonSelections || {}).filter(
      (metric) => comparisonSelections[metric],
    );

    const datasets = selectedMetrics.map((metric, index) => {
      const { machineName } = parseCompositeMetric(metric);
      const apiColor = machineColors[machineName];

      const colorHue = (index * 65) % 360;
      const borderColor = apiColor || `hsl(${colorHue}, 75%, 45%)`;

      // If apiColor is provided, we can use it directly.
      // For background, we need to handle if it's hex to add alpha.
      // Easiest is to use hsla for generated colors, and for API colors we
      // can just use the color with 0.18 opacity if it's hex or similar.
      // But CSS can handle hex + alpha with #RRGGBBAA or using color-mix.
      // Since we want 0.18 opacity:
      let backgroundColor = `hsla(${colorHue}, 75%, 45%, 0.18)`;
      if (apiColor) {
        if (apiColor.startsWith("#")) {
          // simple hex alpha addition (assuming #RRGGBB)
          backgroundColor = apiColor.length === 7 ? `${apiColor}2E` : apiColor; // 2E is ~0.18 in hex
        } else {
          backgroundColor = apiColor; // fallback
        }
      }

      let data = seriesByMetric[metric] || [];

      // Convert data if in PKR mode and metric is Power-related
      if (
        currencyMode === "PKR" &&
        (activeComparisonOption.value === "power_kw" ||
          activeComparisonOption.value === "tap")
      ) {
        data = data.map((v) => (typeof v === "number" ? v * unitPrice : v));
      }

      return {
        label: formatMetricLabel(metric),
        data,
        borderColor,
        backgroundColor,
        tension: 0.35,
        fill: true,
        pointRadius: 4,
      };
    });

    // For Energy metric, sort machines by total consumption (descending)
    const finalDatasets =
      activeComparisonOption.value === "energy"
        ? [...datasets].sort((a, b) => {
          const sumA = (a.data || []).reduce(
            (acc, v) => acc + (typeof v === "number" ? v : 0),
            0,
          );
          const sumB = (b.data || []).reduce(
            (acc, v) => acc + (typeof v === "number" ? v : 0),
            0,
          );
          return sumB - sumA;
        })
        : datasets;

    return {
      labels: chartData.labels,
      rawLabels: rawLabels,
      datasets: finalDatasets,
      option: activeComparisonOption,
    };
  }, [
    chartData.labels,
    rawLabels,
    availableComparisonMetrics,
    comparisonSelections,
    seriesByMetric,
    activeComparisonOption,
    currencyMode,
    unitPrice,
    machineColors,
  ]);

  const hasComparisonChartData = useMemo(
    () =>
      comparisonSeries.datasets.length > 0 &&
      comparisonSeries.datasets.some((ds) =>
        (ds.data || []).some((value) => Number.isFinite(Number(value))),
      ),
    [comparisonSeries.datasets],
  );

  const comparisonGraphLoading = loading || summaryLoading;
  const comparisonGraphError = error
    ? "Unable to load graph data. Please check the selected range and try again."
    : "";

  const comparisonStatLines = useMemo(() => {
    const singleSeries = comparisonSeries.datasets.length === 1;
    if (!singleSeries || graphType !== "line") return [];
    if (
      selectedMachineNode?.name === "Usage" ||
      (selectedMetric || "").toLowerCase().startsWith("usage_")
    ) {
      return [];
    }

    const primaryDataset = comparisonSeries.datasets[0];
    const primaryValues = primaryDataset?.data || [];
    if (!primaryValues.length) return [];

    const summaryBucket = summaryData?.Consumed || summaryData?.Generated || null;
    if (!summaryBucket) return [];

    const minValue = extractStatValue(summaryBucket.min);
    const maxValue = extractStatValue(summaryBucket.max);
    const averageValue = extractStatValue(summaryBucket.avg);

    return [
      {
        label: "Max",
        value: maxValue,
        timestamp: extractStatTimestamp(summaryBucket.max),
        color: "#ef4444",
        dashArray: 8,
      },
      {
        label: "Average",
        value: averageValue,
        timestamp: findClosestTimestamp(
          comparisonSeries.rawLabels,
          primaryValues,
          averageValue,
        ),
        color: "#f59e0b",
        dashArray: 8,
      },
      {
        label: "Min",
        value: minValue,
        timestamp: extractStatTimestamp(summaryBucket.min),
        color: "#3b82f6",
        dashArray: 8,
      },
    ].filter((line) => Number.isFinite(Number(line.value)));
  }, [comparisonSeries, graphType, selectedMachineNode, selectedMetric, summaryData]);

  // Sync comparisonSelections and trigger refetch when selectedMachines or comparisonMetric changes
  useEffect(() => {
    if (!selectedMachines || selectedMachines.length === 0) {
      setComparisonSelections({});
      // None should clear chart locally without hitting API
      return;
    }

    const next = {};
    const preserveUsage =
      selectedMachineNode?.name === "Usage" ||
      (selectedMetric || "").toLowerCase().startsWith("usage_");
    const normalizedSelectedMachines = preserveUsage
      ? Array.from(new Set(["Usage", ...selectedMachines]))
      : selectedMachines;

    // For each selected machine, try to pick the best matching metric for the current comparisonMetric.
    normalizedSelectedMachines.forEach((machineName) => {
      if (!machineName) return;
      // If machine is Usage, try Usage_* metrics first
      if (machineName === "Usage") {
        const usageMetric = metrics.find((m) => m.toLowerCase().startsWith("usage_"));
        if (usageMetric) {
          next[usageMetric] = true;
          return;
        }
      }

      // 1) Prefer a metric from availableComparisonMetrics that starts with machineName
      let candidate = availableComparisonMetrics.find((m) => m.startsWith(`${machineName}_`));
      // 2) Otherwise, prefer any metric that starts with machineName and matches matchers
      if (!candidate) {
        candidate = metrics.find((m) =>
          m.startsWith(`${machineName}_`) &&
          activeComparisonOption.matchers.some((matcher) => m.toLowerCase().includes(matcher.toLowerCase().trim())),
        );
      }
      // 3) Otherwise, take any metric for the machine
      if (!candidate) {
        candidate = metrics.find((m) => m.startsWith(`${machineName}_`));
      }
      if (candidate) next[candidate] = true;
    });

    setComparisonSelections(next);

    // Trigger API refetch for selected machines (send comma-separated names)
    const selParam = normalizedSelectedMachines.join(",");
    refetch(selParam || "Usage", comparisonMetric);
  }, [selectedMachines, comparisonMetric, availableComparisonMetrics, selectedMachineNode, selectedMetric]);

  const comparisonValueFormatter = useMemo(() => {
    return (value) => {
      if (value == null || isNaN(Number(value))) return "–";

      let unit = activeComparisonOption.unit;

      // Update unit label if in PKR mode for Power metrics
      if (
        currencyMode === "PKR" &&
        (activeComparisonOption.value === "power_kw" ||
          activeComparisonOption.value === "tap")
      ) {
        unit = "PKR";
      }

      const numValue = Number(value);
      const formatted = numValue.toFixed(2);
      return unit ? `${formatted} ${unit}` : formatted;
    };
  }, [activeComparisonOption, currencyMode]);

  const handleToggleMetric = (metric) => {
    setComparisonSelections((prev) => ({
      ...prev,
      [metric]: !prev[metric],
    }));
  };

  const handleDateChange = (event) => {
    const { name, value } = event.target;
    if (!value) return;
    setDateRangeFromStrings({
      start: name === "start" ? value : formatDateTimeLocal(dateRange.start),
      end: name === "end" ? value : formatDateTimeLocal(dateRange.end),
    });
  };

  const handleGraphDateChange = (event) => {
    const { name, value } = event.target;
    if (!value) return;

    if (mode === "minute") {
      const maxSpanMs = 24 * 60 * 60 * 1000;
      const now = new Date();

      if (name === "start") {
        const startDate = new Date(value);
        if (Number.isNaN(startDate.getTime())) return;

        const safeStart = startDate > now ? now : startDate;
        let endDate = graphDateRange.end;
        if (
          endDate <= safeStart ||
          endDate.getTime() - safeStart.getTime() > maxSpanMs
        ) {
          endDate = new Date(
            Math.min(safeStart.getTime() + maxSpanMs, now.getTime()),
          );
        }
        if (endDate > now) endDate = now;

        setGraphDateRangeFromStrings({
          start: formatDateTimeLocal(safeStart),
          end: formatDateTimeLocal(endDate),
        });
      } else {
        setGraphDateRangeFromStrings({
          start: formatDateTimeLocal(graphDateRange.start),
          end: value,
        });
      }
    } else if (mode === "second") {
      // For second mode, when start changes, set end to 1 hour later
      if (name === "start") {
        const startDate = new Date(value);
        if (Number.isNaN(startDate.getTime())) return;

        const now = new Date();
        const safeStartDate = startDate > now ? now : startDate;
        const endDate = new Date(safeStartDate.getTime() + 60 * 60 * 1000); // 1 hour later
        const safeEndDate = endDate > now ? now : endDate;

        setGraphDateRangeFromStrings({
          start: formatDateTimeLocal(safeStartDate),
          end: formatDateTimeLocal(safeEndDate),
        });
      } else {
        // If end changes, adjust to be within 1 hour of start
        setGraphDateRangeFromStrings({
          start: formatDateTimeLocal(graphDateRange.start),
          end: value,
        });
      }
    }
  };

  const handleMachineSelect = useCallback((node) => {
    // User selected a machine/node from the hierarchy
    const machineName = node?.name;
    if (!machineName) return;

    if (machineName === "Usage") {
      setSelectedMachineNode(node);
      setSelectedMetric("Usage_power_kw");
      // Ensure API fetch requests Usage with the currently selected comparison metric
      refetch("Usage", comparisonMetric);
      return;
    }

    // 1. Try to find a direct metric match
    const matchingMetric = metrics.find((m) => m.startsWith(machineName));
    if (matchingMetric) {
      setSelectedMetric(matchingMetric);
    }

    // 2. Find the best match based on the current Comparison Option
    const candidate = metrics.find(
      (m) =>
        m.startsWith(machineName) &&
        activeComparisonOption?.matchers?.some((matcher) =>
          m.toLowerCase().includes(matcher.toLowerCase().trim()),
        ),
    );

    if (candidate) {
      setSelectedMetric(candidate);
    } else if (!matchingMetric) {
      // Fallback only if we didn't find anything yet
      const fallback = metrics.find((m) => m.startsWith(machineName));
      if (fallback) setSelectedMetric(fallback);
    }

    // 3. Update the selected node state
    setSelectedMachineNode(node);
    // Trigger data fetch filtered to this machine (or Usage aggregate)
    if (node && node.name) {
      refetch(node.name === "Usage" ? "Usage" : node.name, comparisonMetric);
    }
  }, [metrics, activeComparisonOption, setSelectedMetric, setSelectedMachineNode]);

  const handleRefresh = () => {
    // Force refresh for all data hooks
    // Build selected machines param from comparison selections when available
    const selectedMetricsList = Object.keys(comparisonSelections || {}).filter((k) => comparisonSelections[k]);
    const selectedNames = [
      ...new Set(
        selectedMetricsList
          .map((m) => parseCompositeMetric(m).machineName)
          .filter(Boolean),
      ),
    ];
    const selParam = selectedNames.length ? selectedNames.join(",") : (selectedMachineNode?.name || "Usage");
    refetch(selParam, comparisonMetric); // Analytics Refetch with selected machines and metric
    refetchHeatmap(true); // Heatmap Refetch (Forced)
    // Summary data is also refreshed
    if (selectedMetric) {
      const { machineName } = parseCompositeMetric(selectedMetric);
      fetchSummaryData(machineName, comparisonMetric);
    }
  };

  const pdfScopeLabel = useMemo(() => {
    const isWholeFacility =
      !selectedMachineNode?.name || selectedMachineNode.name === "Usage";
    return isWholeFacility
      ? "Whole Facility (Usage)"
      : selectedMachineNode.name;
  }, [selectedMachineNode]);

  const handleOpenPdfReportDialog = () => {
    const username = localStorage.getItem("username") || "";
    if (!username) {
      window.alert("Please log in again to download the report.");
      return;
    }
    setPdfDialogOpen(true);
  };

  const handleDownloadEnergyReport = async (reportDateRange) => {
    const username = localStorage.getItem("username") || "";
    if (!username) {
      window.alert("Please log in again to download the report.");
      return;
    }

    if (!reportDateRange?.start || !reportDateRange?.end) {
      window.alert("Select a valid date range in the dialog.");
      return;
    }

    const isWholeFacility =
      !selectedMachineNode?.name || selectedMachineNode.name === "Usage";
    const scopeLabel = pdfScopeLabel;

    setPdfReportLoading(true);
    try {
      const scopeNodeName = isWholeFacility ? null : selectedMachineNode.name;
      const scopeCategory = selectedMachineNode?.category || "USAGE";
      const [hierarchyData, dailySeries, heatmapData, pricing, scopeSummary] =
        await Promise.all([
          fetchEnergyHierarchy(username, reportDateRange),
          fetchDailyEnergySeries(username, reportDateRange, scopeNodeName),
          fetchHeatmapEnergyData(username, reportDateRange, scopeNodeName),
          fetchReportPricing(username),
          fetchScopeSummary(username, reportDateRange, scopeNodeName),
        ]);

      const rows = buildEnergyBreakdownRows(hierarchyData, scopeNodeName);
      const costRows = buildCostRows(rows, pricing);
      const scopeCostSummary = buildScopeCostSummary(
        scopeCategory,
        scopeSummary,
        pricing,
      );

      if (
        !rows.length &&
        !dailySeries.length &&
        !hierarchyData &&
        !heatmapData?.rows?.length
      ) {
        window.alert("No energy data found for the selected range and scope.");
        return;
      }

      await generateEnergyBreakdownPdf({
        rows,
        dailySeries,
        hierarchyData,
        scopeNodeName,
        heatmapData,
        costData: {
          costRows,
          scopeCostSummary,
        },
        metadata: {
          username,
          scopeLabel,
          start: reportDateRange.start,
          end: reportDateRange.end,
        },
      });
      setPdfDialogOpen(false);
    } catch (err) {
      console.error("Energy report PDF failed:", err);
      window.alert("Failed to generate the energy report PDF. Please try again.");
    } finally {
      setPdfReportLoading(false);
    }
  };

  const handleSelectAll = () => {
    const names = machinesList.map((m) => m.name);
    setSelectAllActive(true);
    setSelectedMachines(names);
    setUserSelectionLocked(true);
  };

  const handleSelectNone = () => {
    setSelectAllActive(false);
    setSelectedMachines([]);
    setComparisonSelections({});
    setUserSelectionLocked(true);
  };

  // 4. Default to first GRID machine, then first MACHINE category, if nothing is selected or if we just loaded
  useEffect(() => {
    if (machinesList.length > 0 && !selectedMachineNode) {
      // Only auto-select defaults if user hasn't interacted with selection yet
      if (userSelectionLocked) return;

      const getPriorityMatch = (cat) =>
        machinesList.find((m) => (m.category || "").toUpperCase() === cat);

      const firstGrid = getPriorityMatch("GRID");
      const firstMachine = getPriorityMatch("MACHINE");

      if (firstGrid) {
        handleMachineSelect(firstGrid);
        refetch(firstGrid.name === "Usage" ? "Usage" : firstGrid.name, comparisonMetric);
      } else if (firstMachine) {
        handleMachineSelect(firstMachine);
        refetch(firstMachine.name, comparisonMetric);
      } else if (machinesList[0]) {
        handleMachineSelect(machinesList[0]);
        refetch(machinesList[0].name, comparisonMetric);
      }

      // After first auto-select, lock to avoid overriding user actions
      setUserSelectionLocked(true);
    }
  }, [machinesList, selectedMachineNode, handleMachineSelect]);

  // When comparison metric changes, refresh the line graph to request new metric from API
  useEffect(() => {
    if (!selectedMachineNode) return;
    const sel = selectedMachineNode.name === "Usage" ? "Usage" : selectedMachineNode.name;
    refetch(sel, comparisonMetric);
  }, [comparisonMetric, selectedMachineNode]);

  return (
    <div className="historical-container">
      <EnergyReportPdfDialog
        isOpen={pdfDialogOpen}
        onClose={() => {
          if (!pdfReportLoading) setPdfDialogOpen(false);
        }}
        onGenerate={handleDownloadEnergyReport}
        loading={pdfReportLoading}
        scopeLabel={pdfScopeLabel}
      />
      <div className="pdf-report-tab-wrap">
        <button
          type="button"
          onClick={handleOpenPdfReportDialog}
          className="pdf-report-tab"
          disabled={pdfReportLoading}
          aria-label="Open energy report PDF dialog"
        >
          <span className="pdf-report-tab-panel">
            <span className="pdf-report-tab-title">
              {pdfReportLoading ? "Generating…" : "Energy Report"}
            </span>
            <span className="pdf-report-tab-subtitle">
              {pdfReportLoading
                ? "Please wait"
                : "Select dates and download PDF"}
            </span>
          </span>
          <span className="pdf-report-tab-rail" aria-hidden="true">
            {pdfReportLoading ? (
              <span className="pdf-report-tab-spinner" />
            ) : (
              <svg
                className="pdf-report-tab-icon"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14 2H8C6.9 2 6 2.9 6 4V20C6 21.1 6.9 22 8 22H16C17.1 22 18 21.1 18 20V8L14 2Z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 2V8H18M9 13H15M9 17H13"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            <span className="pdf-report-tab-label">PDF Report</span>
          </span>
        </button>
      </div>
      {isAnyGraphLoading && (
        <div className="historical-global-loader-overlay" role="status" aria-live="polite">
          <div className="historical-global-loader-content">
            <div className="historical-lightning-loader" aria-hidden="true">
              <svg viewBox="0 0 160 247" focusable="false">
                <defs>
                  <linearGradient
                    id="progress"
                    x1="80"
                    y1="241.5"
                    x2="80"
                    y2="5.5"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset="0%" stopColor="#009F8D" stopOpacity="1" />
                    <stop offset="0%" stopColor="#009F8D" stopOpacity="1">
                      <animate
                        attributeName="offset"
                        values="0;0.76;1;1;0"
                        keyTimes="0;0.5;0.72;0.86;1"
                        dur="2.2s"
                        repeatCount="indefinite"
                        calcMode="spline"
                        keySplines="0.4 0 0.2 1;0.4 0 0.2 1;0.4 0 0.2 1;0.4 0 0.2 1"
                      />
                    </stop>
                    <stop offset="0%" stopColor="#009F8D" stopOpacity="0">
                      <animate
                        attributeName="offset"
                        values="0;0.76;1;1;0"
                        keyTimes="0;0.5;0.72;0.86;1"
                        dur="2.2s"
                        repeatCount="indefinite"
                        calcMode="spline"
                        keySplines="0.4 0 0.2 1;0.4 0 0.2 1;0.4 0 0.2 1;0.4 0 0.2 1"
                      />
                    </stop>
                    <stop offset="100%" stopColor="#009F8D" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  className="historical-bolt-shape"
                  fill="url(#progress)"
                  id="my-shape"
                  stroke="#009F8D"
                  strokeWidth="10"
                  strokeLinejoin="round"
                  d="M137.641 5.5H60.1789L5.5 135.194H67.9251L32.8394 241.5L154.5 86.718H98.9098L137.641 5.5Z"
                />
              </svg>
            </div>
          </div>
        </div>
      )}
      {/* 1. Header & Controls Section */}
      <section className="control-panel">
        {/* Simplified Machine Selector */}
        <div className="control-row">
          <div
            className="control-group"
            style={{
              flex: "1 1 auto",
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "nowrap",
              minWidth: 0,
            }}
          >
            {/* Date/Time Pickers - Different for Second/Minute modes */}
            {mode === "second" || mode === "minute" ? (
              <div className="date-input-wrapper">
                {mode === "second" && (
                  <label
                    style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#475569",
                      marginRight: 8,
                    }}
                  >
                    Select Hour:
                  </label>
                )}
                <input
                  type="datetime-local"
                  name="start"
                  value={formatDateTimeLocal(graphDateRange.start)}
                  onChange={handleGraphDateChange}
                  onClick={(e) => e.target.showPicker?.()}
                  max={formatDateTimeLocal(new Date())}
                  step={mode === "minute" ? "60" : "1"}
                  className="clean-date-input"
                  style={{ width: "240px" }}
                />
                <span style={{ color: "#cbd5e1", fontWeight: 700 }}>
                  TO
                </span>
                <input
                  type="datetime-local"
                  name="end"
                  value={formatDateTimeLocal(graphDateRange.end)}
                  onChange={handleGraphDateChange}
                  onClick={(e) => e.target.showPicker?.()}
                  max={formatDateTimeLocal(new Date())}
                  step={mode === "minute" ? "60" : "1"}
                  className="clean-date-input"
                  style={{ width: "240px" }}
                />
              </div>
            ) : (
              <div className="date-input-wrapper">
                <input
                  type="datetime-local"
                  name="start"
                  value={formatDateTimeLocal(dateRange.start)}
                  onChange={handleDateChange}
                  onClick={(e) => e.target.showPicker?.()}
                  max={formatDateTimeLocal(new Date())}
                  step="1"
                  className="clean-date-input"
                  style={{ width: "240px" }}
                />
                <span
                  style={{ color: "#cbd5e1", fontWeight: 700, margin: "0 4px" }}
                >
                  TO
                </span>
                <input
                  type="datetime-local"
                  name="end"
                  value={formatDateTimeLocal(dateRange.end)}
                  onChange={handleDateChange}
                  onClick={(e) => e.target.showPicker?.()}
                  step="1"
                  className="clean-date-input"
                  style={{ width: "240px" }}
                />
                <div style={{ display: "flex", gap: 4, marginLeft: "4px" }}>
                  {quickRanges.map((range) => (
                    <button
                      key={range.value}
                      type="button"
                      onClick={() => applyQuickRange(range.value)}
                      className="mini-chip"
                      style={{ padding: "8px 14px" }}
                    >
                      {range.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Machine and Metric Selectors */}
          {/* Hide metric dropdown if Usage is selected */}
          {selectedMachineNode?.name !== "Usage" && (
            <div className="control-group" style={{ flex: "0 1 auto", minWidth: 0 }}>
              <select
                value={comparisonMetric}
                onChange={(e) => setComparisonMetric(e.target.value)}
                className="clean-select"
              >
                {comparisonMetricOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="control-group" style={{ flex: "0 1 auto", minWidth: 0 }}>
            <select
              value={
                selectedMachineNode?.name ||
                (selectedMetric ? parseCompositeMetric(selectedMetric).machineName : "")
              }
              onChange={(e) => {
                const name = e.target.value;
                const selected = machinesList.find((n) => n.name === name);
                // Make the select input act as single-selection: replace selected machines
                setSelectAllActive(false);
                setSelectedMachines([name]);
                setUserSelectionLocked(true);
                handleMachineSelect(selected);
                // If Usage is selected, set metric to power_kw for summary
                if (name === "Usage") {
                  setComparisonMetric("power_kw");
                  setSelectedMetric("Usage_power_kw");
                }
              }}
              className="clean-select text-ellipsis"
              style={{ minWidth: "150px", maxWidth: "100%" }}
            >
              {machinesList.map((node) => (
                <option key={node.name} value={node.name}>
                  {node.name === "Usage" ? "Usage (Total Consumption)" : node.name}
                  {node.category && node.category !== "Unknown" && node.name !== "Usage"
                    ? ` (${node.category})`
                    : ""}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginLeft: "auto", display: "flex", gap: "8px", alignItems: "center" }}>
            <button
              type="button"
              onClick={handleRefresh}
              className="refresh-button"
              style={{ padding: "10px 16px", flexShrink: 0 }}
              disabled={loading || heatmapLoading || pdfReportLoading}
            >
              {loading || heatmapLoading ? (
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="spinner"></span> Refreshing...
                </span>
              ) : (
                "Refresh"
              )}
            </button>

            {/* Currency Toggle */}
            <div
              style={{
                display: "flex",
                background: "#f1f5f9",
                padding: 2,
                borderRadius: 12,
                flexShrink: 0,
              }}
            >
              {["Energy", "PKR"].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setCurrencyMode(mode)}
                  style={{
                    padding: "10px 16px",
                    borderRadius: 9,
                    border: "none",
                    backgroundColor:
                      currencyMode === mode
                        ? mode === "PKR"
                          ? "#1BA08E"
                          : "#3b82f6"
                        : "transparent",
                    color: currencyMode === mode ? "#fff" : "#64748b",
                    fontWeight: currencyMode === mode ? 700 : 500,
                    cursor: "pointer",
                    boxShadow:
                      currencyMode === mode
                        ? "0 4px 12px -2px rgba(0,0,0,0.15)"
                        : "none",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    fontSize: 12,
                    whiteSpace: "nowrap",
                  }}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 2. Stats Section - Responsive Tables */}

      {/* Summary Cards Row (Moved above tables) */}
      <section className="summary-cards-section">
        <div className="summary-cards-wrapper">
          {(() => {
            const isPKR = currencyMode === "PKR";
            const category = (selectedMachineNode?.category || "").toUpperCase();
            const brandColors = {
              teal: "#009f8d",
              orange: "#ff9c07",
              deepTeal: "#005051",
              navy: "#002f46",
              lightTeal: "#00c6a9",
            };

            const metricCardInfo = {
              power_kw: { label: "POWER", unit: "kW" },
              energy: { label: "ENERGY", unit: "kWh" },
              voltage: { label: "VOLTAGE", unit: "V" },
              power_factor: { label: "POWER FACTOR", unit: "" },
              current: { label: "CURRENT", unit: "A" },
              frequency: { label: "FREQUENCY", unit: "Hz" },
              THD_V: { label: "THD VOLTAGE", unit: "V" },
              THD_I: { label: "THD CURRENT", unit: "A" },
              TAP: { label: "TAP", unit: "kW" },
            };
            const currentMetricInfo = metricCardInfo[comparisonMetric] || { label: "POWER", unit: "kW" };

            // Check if this is a Grid or Usage category for special energy display
            const isGridOrUsage = category === "GRID" || category === "USAGE" || (selectedMachineNode?.name === "Usage");

            // Cost calculation logic
            let totalCost = 0;
            let estimatedCost = 0;
            if (category === "GENERATOR") {
              totalCost =
                (summaryData?.EnergyGenerated || 0) *
                (isPKR ? unitPrice : 1);
              estimatedCost =
                (summaryData?.EnergyGenerated || 0) * unitPrice;
            } else {
              totalCost = (summaryData?.NetEnergy || 0) * (isPKR ? unitPrice : 1);
              estimatedCost = (summaryData?.NetEnergy || 0) * unitPrice;
            }
            const unit = isPKR ? "PKR" : "kWh";
            const cards = [];

            // First card changes based on category
            if (isGridOrUsage) {
              // Special card for Grid/Usage categories showing Net Energy with Import/Export
              cards.push({
                title: "Net Energy",
                value: summaryData?.NetEnergy || 0,
                color: brandColors.teal,
                isEnergyCard: true,
                import: summaryData?.EnergyConsumed || 0,
                export: summaryData?.EnergyGenerated || 0,
                // Lightning Icon
                icon: (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13 2L3 14H12L11 22L21 10H12L13 2Z"
                      fill={brandColors.teal}
                      stroke={brandColors.teal}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ),
              });
            } else {
              // Default card for other categories
              cards.push({
                title: isPKR ? "Total Cost" : mainEnergyLabel,
                value: totalCost,
                color: brandColors.teal,
                // Lightning Icon
                icon: (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13 2L3 14H12L11 22L21 10H12L13 2Z"
                      fill={brandColors.teal}
                      stroke={brandColors.teal}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ),
              });
            }

            // Always add remaining cards regardless of category
            cards.push(
              {
                title: "Estimated Cost",
                value: estimatedCost,
                color: brandColors.orange,
                unit: "PKR",
                // Coins Icon
                icon: (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="8"
                      cy="8"
                      r="7"
                      stroke={brandColors.orange}
                      strokeWidth="2"
                    />
                    <path
                      d="M11 13C11 13 14.5 14 14.5 17.5C14.5 21 11 22 11 22M11 13V22M11 13C11 13 7.5 12 7.5 8.5C7.5 5 11 4 11 4M11 13V4"
                      stroke={brandColors.orange}
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <circle
                      cx="16"
                      cy="16"
                      r="7"
                      fill={brandColors.orange}
                      stroke={brandColors.orange}
                      strokeWidth="2"
                    />
                  </svg>
                ),
              },
              {
                title: `AVG ${currentMetricInfo.label}`,
                value: summaryData?.Consumed?.avg || 0,
                color: "#8e24aa", // Medium Purple
                unit: currentMetricInfo.unit,
                // Gauge Icon
                icon: (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2 12H4M20 12H22M4 12C4 7.58172 7.58172 4 12 4C16.4183 4 20 7.58172 20 12M12 12V8"
                      stroke="#8e24aa"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ),
              },
              {
                title: `MIN ${currentMetricInfo.label}`,
                value:
                  typeof summaryData?.Consumed?.min === "object"
                    ? summaryData?.Consumed?.min?.value
                    : summaryData?.Consumed?.min || 0,
                timestamp:
                  typeof summaryData?.Consumed?.min === "object"
                    ? summaryData?.Consumed?.min?.timestamp
                    : null,
                color: "#1e88e5", // Medium Blue
                unit: currentMetricInfo.unit,
                // Graph Down
                icon: (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 6L10 12L14 8L20 18"
                      stroke="#1e88e5"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M20 18H16"
                      stroke="#1e88e5"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M20 18V14"
                      stroke="#1e88e5"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ),
              },
              {
                title: `MAX ${currentMetricInfo.label}`,
                value:
                  typeof summaryData?.Consumed?.max === "object"
                    ? summaryData?.Consumed?.max?.value
                    : summaryData?.Consumed?.max || 0,
                timestamp:
                  typeof summaryData?.Consumed?.max === "object"
                    ? summaryData?.Consumed?.max?.timestamp
                    : null,
                color: brandColors.lightTeal,
                unit: currentMetricInfo.unit,
                // Graph Up
                icon: (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 18L10 12L14 16L20 6"
                      stroke={brandColors.lightTeal}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M20 6H16"
                      stroke={brandColors.lightTeal}
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M20 6V10"
                      stroke={brandColors.lightTeal}
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ),
              },
            );

            const mdiValueInPKR = summaryData?.mdi_value ?? summaryData?.mdi ?? mdiData?.mdi_value ?? 0;
            const mdiValueInKW = mdiData?.mdi_reference_kw ?? summaryData?.mdi_reference_kw ?? 0;
            const parsedLpf = Number(lpfData?.lowest_power_factor);
            const lpfRatio = Number.isFinite(parsedLpf) ? parsedLpf : 0;
            const lpfValueInPKR = unitPrice * lpfRatio;

            cards.push({
              title: "MDI",
              value: isPKR ? mdiValueInPKR : mdiValueInKW,
              color: "#e11d48", // Rose Red
              unit: isPKR ? "PKR" : "kW",
              tooltip: "Maximum Demand Indicator: The highest minimum load (kW) sustained over any continuous 30-minute window.",
              icon: (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 20V10M12 20V4M6 20V14"
                    stroke="#e11d48"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 4h20"
                    stroke="#e11d48"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeDasharray="4 4"
                  />
                  <circle cx="12" cy="4" r="2" fill="#e11d48" />
                </svg>
              ),
            });

            cards.push({
              title: "LPF",
              value: isPKR ? lpfValueInPKR : lpfRatio,
              color: "#d97706", // Amber
              unit: isPKR ? "PKR" : "",
              tooltip: "Lowest Power Factor: The lowest average power factor value recorded during the selected period.",
              icon: (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22Z"
                    stroke="#d97706"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 8V13L15.5 15.5"
                    stroke="#d97706"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16 4L20 8"
                    stroke="#d97706"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ),
            });

            return cards.map((card, idx) => (
              <div
                key={idx}
                className={`summary-card ${card.isEnergyCard ? 'energy-card' : ''}`}
                title={card.tooltip}
                style={{
                  backgroundColor: `${card.color}15`,
                  border: `1px solid ${card.color}30`,
                  ...(card.isEnergyCard && { minheight: 150, flex: '0 0 auto' })
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    className="summary-card-title"
                    style={{ color: card.color, opacity: 0.9 }}
                  >
                    {card.title}
                  </div>
                  <div style={{ opacity: 1 }}>{card.icon}</div>
                </div>
                <div
                  className="summary-card-value"
                  style={{ color: card.color }}
                >
                  {loading || summaryLoading ? (
                    <span
                      className="spinner"
                      style={{
                        borderTopColor: card.color,
                        width: 20,
                        height: 20,
                        borderWidth: 3,
                      }}
                    ></span>
                  ) : card.isEnergyCard ? (
                    <div>
                      {/* Line 1: Main Value */}
                      <div style={{ fontSize: "28px", fontWeight: "800", color: card.color }}>
                        {Number(card.value || 0).toFixed(2)}
                        <span style={{ fontSize: "17px", fontWeight: "600", color: card.color, marginLeft: "6px" }}>
                          kWh
                        </span>
                      </div>

                      {/* Line 2: Import & Export Values */}
                      <div style={{ display: "flex", marginTop: "8px" }}>
                        <span style={{ fontSize: "12px", fontWeight: "700", flex: "1", color: "#e11d48" }}>
                          {Number(card.import || 0).toFixed(2)} <span style={{ fontSize: "9px", fontWeight: "500", color: "#e11d48" }}>kWh</span>
                        </span>
                        <span style={{ fontSize: "12px", fontWeight: "700", color: card.color }}>
                          {Number(card.export || 0).toFixed(2)} <span style={{ fontSize: "9px", fontWeight: "500", color: card.color }}>kWh</span>
                        </span>
                      </div>

                      {/* Line 3: Labels */}
                      <div style={{ display: "flex", gap: "12px", fontSize: "12px", fontWeight: "600", color: "#6b7280" }}>
                        <span style={{ color: "#e11d48", flex: "1" }}>Import</span>
                        <span style={{ color: card.color }}>Export</span>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <div>
                        {Number(card.value || 0).toFixed(2)}
                        <span
                          className="summary-card-unit"
                          style={{ color: card.color, opacity: 0.8 }}
                        >
                          {card.unit ?? unit}
                        </span>
                      </div>
                      {card.timestamp && (
                        <div
                          style={{
                            fontSize: "11px",
                            color: `${card.color}99`,
                            fontWeight: 600,
                            marginTop: "2px",
                          }}
                        >
                          {new Date(card.timestamp).toLocaleString(undefined, {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ));
          })()}
        </div>
      </section>

      {/* 3. Charts & Analytics Section */}
      <section
        className="analytics-layout-container"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          alignItems: "stretch",
        }}
      >
        <div
          className="analytics-main-card"
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Enhanced Header */}
          <div
            style={{
              padding: "16px 20px 0 20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "transparent",
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flex: 1,
                minWidth: 0,
              }}
            >
              {/* Redundant title removed as the metric selector is inline */}

              {/* Select All/None Actions */}
              <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                <button
                  onClick={handleSelectAll}
                  style={{
                    fontSize: "9px",
                    padding: "2px 8px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    background: "#f8fafc",
                    cursor: "pointer",
                    fontWeight: 700,
                    color: "#475569",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => (e.target.style.background = "#fff")}
                  onMouseLeave={(e) => (e.target.style.background = "#f8fafc")}
                  type="button"
                >
                  ALL
                </button>
                <button
                  onClick={handleSelectNone}
                  style={{
                    fontSize: "9px",
                    padding: "2px 8px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    background: "#f8fafc",
                    cursor: "pointer",
                    fontWeight: 700,
                    color: "#475569",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => (e.target.style.background = "#fff")}
                  onMouseLeave={(e) => (e.target.style.background = "#f8fafc")}
                  type="button"
                >
                  NONE
                </button>
              </div>
            </div>

            {/* View Mode Toggle (Chart vs Heatmap) */}
            <div
              style={{
                display: "flex",
                background: "#f1f5f9",
                padding: 2,
                borderRadius: 8,
              }}
            >
              {[
                { id: "chart", label: "ANALYSIS", icon: "📈" },
                { id: "heatmap", label: "HEATMAP", icon: "🌡️" },
              ].map((v) => (
                <button
                  key={v.id}
                  onClick={() => setViewMode(v.id)}
                  style={{
                    padding: "4px 10px",
                    borderRadius: 6,
                    border: "none",
                    backgroundColor: viewMode === v.id ? "#fff" : "transparent",
                    color: viewMode === v.id ? "#0f172a" : "#64748b",
                    fontSize: "10px",
                    fontWeight: 700,
                    cursor: "pointer",
                    boxShadow:
                      viewMode === v.id ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <span>{v.icon}</span>
                  {v.label}
                </button>
              ))}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexShrink: 0,
                background: "#f1f5f9",
                padding: 2,
                borderRadius: 8,
              }}
            >
              {["second", "minute", "hour", "day"].map((m) => {
                const isDisabled =
                  (m === "minute" || m === "second") && graphType === "bar";
                return (
                  <button
                    key={m}
                    onClick={() => !isDisabled && setMode(m)}
                    disabled={isDisabled}
                    style={{
                      padding: "4px 8px",
                      borderRadius: 6,
                      border: "none",
                      backgroundColor: mode === m ? "#fff" : "transparent",
                      color: mode === m ? "#0f172a" : "#64748b",
                      fontSize: "10px",
                      fontWeight: 700,
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      opacity: isDisabled ? 0.4 : 1,
                      boxShadow:
                        mode === m ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                    }}
                  >
                    {m.toUpperCase()}
                  </button>
                );
              })}
            </div>

            {/* Graph Type Toggle */}
            <div
              style={{
                display: "flex",
                background: "#f1f5f9",
                padding: 2,
                borderRadius: 8,
              }}
            >
              {["line", "bar"].map((t) => {
                const isDisabled =
                  (t === "bar" && (mode === "minute" || mode === "second")) ||
                  (t === "line" && isEnergyMetric);
                return (
                  <button
                    key={t}
                    onClick={() => !isDisabled && setGraphType(t)}
                    disabled={isDisabled}
                    style={{
                      padding: "4px 8px",
                      borderRadius: 6,
                      border: "none",
                      backgroundColor:
                        graphType === t ? "#fff" : "transparent",
                      color: graphType === t ? "#0f172a" : "#64748b",
                      fontSize: "10px",
                      fontWeight: 700,
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      opacity: isDisabled ? 0.4 : 1,
                      boxShadow:
                        graphType === t
                          ? "0 1px 3px rgba(0,0,0,0.1)"
                          : "none",
                    }}
                  >
                    {t.toUpperCase()}
                  </button>
                );
              })}
            </div>

            {/* Burger Menu for Export */}
            <div style={{ borderLeft: "1px solid #e2e8f0", paddingLeft: 8 }}>
              <ChartBurgerMenu
                chartTitle={`${activeComparisonOption.label}_Analytics`}
                chartRef={comparisonChartRef}
                exportContainer={true}
                chartData={{
                  labels: comparisonSeries.labels,
                  datasets: comparisonSeries.datasets.map((d) => ({
                    label: d.label,
                    data: d.data,
                  })),
                  unit: activeComparisonOption.unit,
                }}
                chartType={viewMode === "chart" ? graphType : "heatmap"}
              />
            </div>
          </div>
          <div
            className="comparison-chart-container"
            ref={comparisonChartRef}
            style={{
              padding: "0 20px",
              minHeight: "500px",
              flex: 1,
              background: "#fff",
              position: "relative",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {viewMode === "heatmap" && heatmapLoading ? (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255, 255, 255, 0.9)",
                  zIndex: 10,
                  borderRadius: "8px",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    border: "4px solid #e2e8f0",
                    borderTopColor: "#3b82f6",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
                <div
                  style={{
                    fontSize: "14px",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Loading heatmap...
                </div>
              </div>
            ) : null}

            <div style={{ flex: 1, minHeight: 400, position: 'relative' }}>
              {viewMode === "chart" ? (
                comparisonGraphLoading ? (
                  <ChartStatus
                    type="loading"
                    title={mode === "second" ? "Loading second-wise graph data..." : "Loading graph data..."}
                    message="Please wait while the selected range is prepared."
                  />
                ) : comparisonGraphError ? (
                  <ChartStatus
                    type="error"
                    title="Graph data could not be loaded"
                    message={comparisonGraphError}
                    actionLabel="Retry"
                    onAction={handleRefresh}
                  />
                ) : hasComparisonChartData ? (
                  <ErrorBoundary>
                    <ApexGraph
                      id="comparison-analytics-chart"
                      labels={comparisonSeries.labels}
                      rawLabels={comparisonSeries.rawLabels}
                      datasets={comparisonSeries.datasets}
                      statLines={comparisonStatLines}
                      chartType={graphType}
                      valueFormatter={comparisonValueFormatter}
                      showBrush={true}
                      mode={mode}
                      metricUnit={activeComparisonOption.unit}
                    />
                  </ErrorBoundary>
                ) : (
                  <ChartStatus
                    type="empty"
                    title={
                      availableComparisonMetrics.length === 0
                        ? "No data available in this range"
                        : "Select metrics below to view chart"
                    }
                    message={
                      availableComparisonMetrics.length === 0
                        ? "Try a different date range or machine."
                        : "Choose one or more metrics to draw the graph."
                    }
                  />
                )
              ) : (
                <div className="heatmap-wrapper" style={{ height: "100%", border: "none", boxShadow: "none", marginTop: 0 }}>
                  <HeatmapChart
                    rows={heatmapRows}
                    timeLabels={heatmapTimeLabels}
                    minValue={heatmapMin}
                    maxValue={heatmapMax}
                    loading={heatmapLoading}
                    machineColor={(() => {
                      const name = selectedMachineNode?.name;
                      let color = name ? machineColors?.[name] : undefined;
                      if (!color || color === "NA" || color === "na") color = "#ef4444";
                      return color;
                    })()}
                    metricLabel={selectedMachineNode?.name || ""}
                    valueFormatter={(value) => {
                      let unit = getUnitForMetric(selectedMetric);
                      if (
                        currencyMode === "PKR" &&
                        (activeComparisonOption.value.toLowerCase() === "power_kw" ||
                          activeComparisonOption.value.toLowerCase() === "tap")
                      ) {
                        unit = "PKR";
                      }
                      return value == null
                        ? "–"
                        : unit === "pf"
                          ? value.toFixed(3)
                          : `${Number(value).toFixed(1)} ${unit}`;
                    }}
                  />
                  {!hasHeatmapData && !heatmapLoading && (
                    <div
                      style={{
                        padding: 40,
                        textAlign: "center",
                        color: "#94a3b8",
                        fontSize: 14,
                      }}
                    >
                      No heatmap insights available for this period.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Machine selection badges moved inside the card - only show in Analysis mode */}
            {viewMode === "chart" && (
              <div style={{ width: "100%", padding: "16px 0 20px 0", borderTop: '1px solid #f1f5f9', backgroundColor: '#fff', zIndex: 5 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                  <div style={{ color: "#64748b", fontSize: 12, fontWeight: 600 }}>Selected Machines: {selectedMachines.length}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, position: "relative" }}>
                  <button
                    onClick={() => scrollMachineList("left")}
                    className="scroll-arrow-button"
                    style={{
                      flexShrink: 0,
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      border: "1px solid #e2e8f0",
                      background: "#fff",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                      color: "#64748b",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#cbd5e1")}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e2e8f0")}
                    type="button"
                    aria-label="Scroll left"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </button>

                  <div
                    ref={machineListRef}
                    className="no-scrollbar"
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "nowrap",
                      overflowX: "auto",
                      padding: "4px 0",
                      flex: 1,
                      scrollBehavior: "smooth",
                    }}
                  >
                    {machinesList.map((node) => {
                      const isActive = selectedMachines.includes(node.name);
                      const color = machineColors[node.name] || "#64748b";
                      return (
                        <button
                          key={node.name}
                          onClick={() => {
                            if (isActive) setSelectedMachines((prev) => prev.filter((p) => p !== node.name));
                            else setSelectedMachines((prev) => [...prev, node.name]);
                            setSelectAllActive(false);
                            setUserSelectionLocked(true);
                          }}
                          className={`metric-badge ${isActive ? "active" : ""}`}
                          style={{
                            padding: "6px 12px",
                            borderRadius: 8,
                            border: isActive ? `1px solid ${color}` : "1px solid #e6eef6",
                            background: isActive ? (color.startsWith("#") ? `${color}1A` : color) : "#fff",
                            color: isActive ? color : "#475569",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                          }}
                        >
                          {node.name}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => scrollMachineList("right")}
                    className="scroll-arrow-button"
                    style={{
                      flexShrink: 0,
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      border: "1px solid #e2e8f0",
                      background: "#fff",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                      color: "#64748b",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#cbd5e1")}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e2e8f0")}
                    type="button"
                    aria-label="Scroll right"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>


        <div style={{ width: "100%", display: "flex" }}>
          <section className="tables-wrapper">
            <OnOffChart
              username={localStorage.getItem("username")}
              dateRange={stableDateRange}
              machinesList={machinesList}
              onLoadingChange={(isLoading) =>
                updateChildGraphLoading("onOffChart", isLoading)
              }
            />
            <DrillDownPieChart
              username={localStorage.getItem("username")}
              dateRange={stableDateRange}
              onLoadingChange={(isLoading) =>
                updateChildGraphLoading("drillDownPieChart", isLoading)
              }
            />

            {/* Table 2: Metric Statistics
            <div className="analytics-table-card">
              <table className="dynamic-table">
                <thead>
                  <tr>
                    <th></th>
                    {columnVisibility.showGenerated && <th>Generated</th>}
                    {columnVisibility.showConsumed && <th>Consumed</th>}
                  </tr>
                </thead>
                <tbody>
                  {summaryData ? (
                    <>
                      <tr>
                        <td className="row-label">Average</td>
                        {columnVisibility.showGenerated && (
                          <td>
                            {(summaryData.Generated?.avg || 0).toFixed(2)}
                          </td>
                        )}
                        {columnVisibility.showConsumed && (
                          <td>{(summaryData.Consumed?.avg || 0).toFixed(2)}</td>
                        )}
                      </tr>
                      <tr>
                        <td className="row-label">Minimum</td>
                        {columnVisibility.showGenerated && (
                          <td>
                            {Number(
                              typeof summaryData?.Generated?.min === "object"
                                ? summaryData?.Generated?.min?.value
                                : summaryData?.Generated?.min || 0,
                            ).toFixed(2)}
                          </td>
                        )}
                        {columnVisibility.showConsumed && (
                          <td>
                            {Number(
                              typeof summaryData?.Consumed?.min === "object"
                                ? summaryData?.Consumed?.min?.value
                                : summaryData?.Consumed?.min || 0,
                            ).toFixed(2)}
                          </td>
                        )}
                      </tr>
                      <tr>
                        <td className="row-label">Maximum</td>
                        {columnVisibility.showGenerated && (
                          <td>
                            {Number(
                              typeof summaryData?.Generated?.max === "object"
                                ? summaryData?.Generated?.max?.value
                                : summaryData?.Generated?.max || 0,
                            ).toFixed(2)}
                          </td>
                        )}
                        {columnVisibility.showConsumed && (
                          <td>
                            {Number(
                              typeof summaryData?.Consumed?.max === "object"
                                ? summaryData?.Consumed?.max?.value
                                : summaryData?.Consumed?.max || 0,
                            ).toFixed(2)}
                          </td>
                        )}
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td
                        colSpan={
                          Object.values(columnVisibility).filter(
                            (v) => v && v !== "showNet",
                          ).length + 1
                        }
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          color: "#94a3b8",
                        }}
                      >
                        {summaryLoading || loading
                          ? "Synchronizing system stats..."
                          : "No analytics available for this period"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div> */}
          </section>
        </div>
      </section>

      {(loading || error) && (
        <section className="selection-area" style={{ marginTop: 0 }}>
          <div style={{ display: "flex", gap: 8, marginTop: 0 }}>
            {loading && (
              <span
                className="metric-badge"
                style={{
                  backgroundColor: "#eff6ff",
                  color: "#3b82f6",
                  borderColor: "#bfdbfe",
                }}
              >
                Processing...
              </span>
            )}
            {error && (
              <span
                className="metric-badge"
                style={{
                  backgroundColor: "#fff1f2",
                  color: "#f43f5e",
                  borderColor: "#fecdd3",
                }}
              >
                {error}
              </span>
            )}
          </div>
        </section>
      )}
      {/* Heatmap Section */}
      <section
        className="heatmap-section"
        style={{
          marginTop: 6,
          borderTop: "2px solid #f1f5f9",
        }}
      >
        {/* Sankey Diagram */}
        <div>
          <SankeyDiagram
            username={localStorage.getItem("username")}
            dateRange={stableDateRange}
            onLoadingChange={(isLoading) =>
              updateChildGraphLoading("sankeyDiagram", isLoading)
            }
          />
        </div>

        {/* Solar Plots Section */}
        {solarDetected && (
          <section className="chart-section" style={{ marginBottom: 20 }}>
            {solarMachines.length > 0 && (
              <div
                className="solar-machine-selector"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "16px",
                  backgroundColor: "#f8fafc",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  marginBottom: "20px",
                }}
              >
                <span style={{ fontSize: "14px", fontWeight: "600", color: "#475569" }}>
                  Select Solar Machine:
                </span>
                <select
                  value={selectedSolarMachine}
                  onChange={(e) => setSelectedSolarMachine(e.target.value)}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    backgroundColor: "#ffffff",
                    color: "#1e293b",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                    outline: "none",
                    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                    transition: "border-color 0.15s ease-in-out",
                  }}
                >
                  {solarMachines.map((machine) => (
                    <option key={machine.name} value={machine.name}>
                      {machine.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="summary-cards-wrapper">
              {/* 1. Total Solar Export - Green */}
              <div
                className="summary-card"
                style={{
                  backgroundColor: "#dcfce7", // Light green bg
                  border: "1px solid #bbf7d0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ opacity: 1 }}>
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 21H21"
                        stroke="#15803d" // Dark green
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5 21V7"
                        stroke="#15803d"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M19 21V3"
                        stroke="#15803d"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M10 21V11"
                        stroke="#15803d"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M14 21V5"
                        stroke="#15803d"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle
                        cx="15"
                        cy="16"
                        r="3"
                        fill="#15803d"
                        stroke="#fff"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M15 14V18"
                        stroke="#fff"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                      <path
                        d="M13 16H17"
                        stroke="#fff"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                </div>
                <div
                  className="summary-card-title"
                  style={{ color: "#15803d", marginTop: 12, fontSize: 13 }}
                >
                  Total Grid Export (Units)
                </div>
                <div
                  className="summary-card-value"
                  style={{ color: "#15803d", fontSize: 28 }}
                >
                  {(solarData?.metrics?.Solar_Export || 0).toFixed(2)}{" "}
                  {/* Updated path from Grid_Export to Solar_Export */}
                  <span style={{ fontSize: 16 }}>kWh</span>
                </div>
              </div>

              {/* 2. Total Wapda Import - Red */}
              <div
                className="summary-card"
                style={{
                  backgroundColor: "#fee2e2", // Light red bg
                  border: "1px solid #fecaca",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ opacity: 1 }}>
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 21H21"
                        stroke="#b91c1c" // Dark red
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5 21V7"
                        stroke="#b91c1c"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M19 21V3"
                        stroke="#b91c1c"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M10 21V11"
                        stroke="#b91c1c"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M14 21V5"
                        stroke="#b91c1c"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle
                        cx="15"
                        cy="16"
                        r="3"
                        fill="#b91c1c"
                        stroke="#fff"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M13 16H17"
                        stroke="#fff"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                </div>
                <div
                  className="summary-card-title"
                  style={{ color: "#b91c1c", marginTop: 12, fontSize: 13 }}
                >
                  Total Grid Import (Units)
                </div>
                <div
                  className="summary-card-value"
                  style={{ color: "#b91c1c", fontSize: 28 }}
                >
                  {(solarData?.metrics?.Grid_Import || 0).toFixed(2)}{" "}
                  {/* Updated path */}
                  <span style={{ fontSize: 16 }}>kWh</span>
                </div>
              </div>

              {/* 3. Total Solar Generation - Yellow */}
              <div
                className="summary-card"
                style={{
                  backgroundColor: "#fef9c3", // Light yellow bg
                  border: "1px solid #fde047",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ opacity: 1 }}>
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 4V2"
                        stroke="#ca8a04" // Dark yellow/gold
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5.636 5.636L4.222 4.222"
                        stroke="#ca8a04"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.364 5.636L19.778 4.222"
                        stroke="#ca8a04"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M20 12H22"
                        stroke="#ca8a04"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2 12H4"
                        stroke="#ca8a04"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16C14.2091 16 16 14.2091 16 12"
                        stroke="#ca8a04"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 18H18"
                        stroke="#ca8a04"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
                <div
                  className="summary-card-title"
                  style={{ color: "#ca8a04", marginTop: 12, fontSize: 13 }}
                >
                  Total Solar Production (Units)
                </div>
                <div
                  className="summary-card-value"
                  style={{ color: "#ca8a04", fontSize: 28 }}
                >
                  {(solarProductionData?.data?.reduce((sum, item) => {
                    const val = Number(item.actual_generation_kwh);
                    return Number.isFinite(val) ? sum + val : sum;
                  }, 0) || 0).toFixed(2)}{" "}
                  {/* Show actual solar production from comparison data */}
                  <span style={{ fontSize: 16 }}>kWh</span>
                </div>
              </div>

              {/* 4. Total Load Consumption - Purple */}
              <div
                className="summary-card"
                style={{
                  backgroundColor: "#f3e8ff", // Light purple bg
                  border: "1px solid #d8b4fe",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ opacity: 1 }}>
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="4"
                        y="12"
                        width="4"
                        height="8"
                        rx="1"
                        stroke="#581c87" // Dark purple
                        strokeWidth="2"
                      />
                      <rect
                        x="10"
                        y="8"
                        width="4"
                        height="12"
                        rx="1"
                        stroke="#581c87"
                        strokeWidth="2"
                      />
                      <rect
                        x="16"
                        y="4"
                        width="4"
                        height="16"
                        rx="1"
                        stroke="#581c87"
                        strokeWidth="2"
                      />
                    </svg>
                  </div>
                </div>
                <div
                  className="summary-card-title"
                  style={{ color: "#581c87", marginTop: 12, fontSize: 13 }}
                >
                  Total Load Consumption (Units)
                </div>
                <div
                  className="summary-card-value"
                  style={{ color: "#00008b", fontSize: 28 }}
                >
                  {(solarData?.metrics?.Load_Consumption || 0).toFixed(2)}{" "}
                  {/* Updated path */}
                  <span style={{ fontSize: 16 }}>kWh</span>
                </div>
              </div>
            </div>

            {/* Solar Graph */}
            {(() => {
              // Transform the new API structure for the SolarGraph component
              const formattedSolarData =
                solarData?.data?.["Date & Time"]?.map((time, index) => ({
                  timestamp: time,
                  Grid_Import_kWh:
                    solarData.data["GRID"]?.[index] > 0
                      ? solarData.data["GRID"][index]
                      : 0,
                  Grid_Export_kWh:
                    solarData.data["GRID"]?.[index] < 0
                      ? Math.abs(solarData.data["GRID"][index])
                      : 0,
                  Solar_Generated_kWh: Math.abs(
                    solarData.data["SOLAR"]?.[index] || 0,
                  ),
                })) || [];

              return (
                <SolarGraph
                  data={formattedSolarData}
                  dateRange={stableDateRange}
                  granularity={solarGranularity}
                  onGranularityChange={setSolarGranularity}
                />
              );
            })()}

            {/* Solar Production Comparison */}
            {solarDetected && (
              <SolarProductionComparison
                data={solarProductionData?.data || []}
                dateRange={stableDateRange}
              />
            )}
          </section>
        )}

        <div className="heatmap-wrapper" style={{ display: 'none' }}>
          {/* This section is moved into the main analytics card but kept here (hidden) for now or removed if safe */}
        </div>

        {/* Comparison Graphs Section */}
        <div className="comparison-graphs-section">
          <ComparisonGraph
            machinesList={machinesList}
            title="Machine 1"
            loaderKey="comparisonGraphA"
            onLoadingChange={updateChildGraphLoading}
          />
          <ComparisonGraph
            machinesList={machinesList}
            title="Machine 2"
            loaderKey="comparisonGraphB"
            onLoadingChange={updateChildGraphLoading}
          />
        </div>
      </section>
    </div>
  );
};

const formatDateTimeLocal = (date) => {
  if (!date || isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  const seconds = `${date.getSeconds()}`.padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

const getUnitForMetric = (metricName) => {
  if (!metricName) return "kW";
  const lower = (metricName || "").toLowerCase();
  const option = comparisonMetricOptions.find((opt) =>
    opt.matchers.some((matcher) => lower.includes(matcher)),
  );
  return option ? option.unit : "kW";
};

export default HistoricalTab;
